const STORAGE_KEY = 'cp.auth.token';

/**
 * Where the bearer token lives.
 *
 * `sessionStorage`, deliberately — the single line below is the whole decision,
 * so it is easy to revisit.
 *
 * sessionStorage and localStorage are EQUALLY readable by an XSS payload; the
 * difference between them is persistence, and persistence is exactly what turns
 * a momentary XSS into a durable compromise. A Sanctum personal access token
 * has no refresh flow and no rotation, so whoever holds it has full authority
 * until it expires — and what it unlocks is a client's legal documents. Scoping
 * it to the tab, paired with the backend's 12-hour SANCTUM_TOKEN_EXPIRATION,
 * bounds that exposure. The cost is re-authenticating after closing the
 * browser, which is mild for a task-shaped portal that people visit because an
 * email told them a document is ready. A NEW TAB does not pay it: it borrows
 * the token from an open tab over a BroadcastChannel (see authSync.ts), which
 * shares the session without ever persisting it.
 *
 * To switch to "keep me signed in", change `store` to window.localStorage and
 * add a `storage`-event listener so signing out propagates across tabs.
 *
 * Whatever is chosen here, a token in JS-readable storage is only ever as safe
 * as the app's XSS posture: no dangerouslySetInnerHTML, a strict CSP on the
 * host, and pinned, audited dependencies matter more than this choice does.
 */
function store(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    // Storage disabled by policy.
    return null;
  }
}

export const authStorage = {
  get(): string | null {
    try {
      return store()?.getItem(STORAGE_KEY) ?? null;
    } catch {
      return null;
    }
  },

  set(token: string): void {
    try {
      store()?.setItem(STORAGE_KEY, token);
    } catch {
      // Safari private mode throws on setItem. The session still works for the
      // life of the page; it just won't survive a reload.
    }
  },

  clear(): void {
    try {
      store()?.removeItem(STORAGE_KEY);
    } catch {
      // Nothing useful to do.
    }
  },
};
